import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, FileText, TrendingUp, Home, Globe, Users, DollarSign } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import SEO from "@/components/SEO";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { visible: { transition: { staggerChildren: 0.09 } } };

/* @section: individual-tax */
export default function IndividualTax() {
  const { t } = useLanguage();
  const categories = [{
      icon: <FileText className="w-5 h-5" />,
      titleEn: "Employment & Self-Employment Income",
      titleKo: "고용 및 자영업 소득",
      items: [
        { en: "W-2 Income (Employees)", ko: "W-2 소득 (직장인)" },
        { en: "1099 Income (Contractors, Freelancers)", ko: "1099 소득 (계약직, 프리랜서)" },
        { en: "Schedule C (Sole Proprietor)", ko: "Schedule C (개인사업자)" },
        { en: "Schedule E (S-Corp / Partnership K-1)", ko: "Schedule E (S-Corp / Partnership K-1)" },
      ],
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      titleEn: "Investment & Capital Gains",
      titleKo: "투자 및 자본이득",
      items: [
        { en: "Stock Sales & Capital Gains", ko: "주식 매각 및 자본이득" },
        { en: "Cryptocurrency Reporting", ko: "가상화폐 보고" },
        { en: "RSU & ESPP Tax Treatment", ko: "RSU 및 ESPP 세금 처리" },
        { en: "Wash Sale Rule Compliance", ko: "Wash Sale 규정 준수" },
      ],
    },
    {
      icon: <Home className="w-5 h-5" />,
      titleEn: "Rental & Real Estate",
      titleKo: "임대 및 부동산",
      items: [
        { en: "Rental Property Income & Expenses", ko: "임대 소득 및 비용" },
        { en: "Depreciation Calculations", ko: "감가상각 계산" },
        { en: "Property Sale Reporting", ko: "부동산 매각 보고" },
        { en: "Home Sale Exclusion", ko: "주거용 주택 매각 제외" },
      ],
    },
    {
      icon: <DollarSign className="w-5 h-5" />,
      titleEn: "Deductions & Credits",
      titleKo: "공제 및 세액 공제",
      items: [
        { en: "Itemized vs. Standard Deduction", ko: "항목별 공제 vs. 표준 공제" },
        { en: "Mortgage Interest & Property Tax", ko: "모기지 이자 및 재산세" },
        { en: "Child Tax Credit & Dependent Care", ko: "자녀 세액 공제 및 보육비" },
        { en: "Education Credits (529, AOTC)", ko: "교육 관련 세액 공제" },
      ],
    },
    {
      icon: <Globe className="w-5 h-5" />,
      titleEn: "Foreign Income & Assets",
      titleKo: "해외 소득 및 자산",
      items: [
        { en: "Foreign Income Reporting Overview", ko: "해외 소득 보고 개요" },
        { en: "FBAR & Foreign Account Awareness", ko: "FBAR 및 해외 계좌 신고 인식" },
        { en: "Foreign Tax Credit", ko: "해외 세액 공제" },
        { en: "Treaty Benefits Overview", ko: "조세 조약 혜택 개요" },
      ],
    },
    {
      icon: <Users className="w-5 h-5" />,
      titleEn: "Filing Status Strategy",
      titleKo: "신고 방법 전략",
      items: [
        { en: "Married Filing Jointly vs. Separately", ko: "부부 합산 vs. 개별 신고" },
        { en: "Head of Household Qualification", ko: "세대주 자격 검토" },
        { en: "Prior-Year Amendments (Form 1040-X)", ko: "이전 연도 수정 신고 (Form 1040-X)" },
        { en: "Extension Filing (Form 4868)", ko: "신고 연장 (Form 4868)" },
      ],
    },
  ];

  const process = [
    { en: "Initial consultation to understand your tax situation", ko: "세금 상황 파악을 위한 초기 상담" },
    { en: "Document checklist sent to you — we guide you through every form", ko: "서류 체크리스트 발송 — 모든 양식을 안내해드립니다" },
    { en: "Accurate tax preparation with a thorough review", ko: "철저한 검토를 거친 정확한 세금보고 준비" },
    { en: "Clear explanation of your return before filing", ko: "신고 전 귀하의 신고서에 대한 명확한 설명" },
    { en: "E-filing and confirmation of acceptance", ko: "전자 신고 및 수리 확인" },
    { en: "Available for questions after filing", ko: "신고 후에도 질문에 답변" },
  ];

  return (
    <>
      <SEO
        title="Individual Tax Preparation San Jose | Enrolled Agent | NexBiz Tax Advisor"
        description="Individual Tax Return preparation in San Jose — W-2, 1099, RSU, Stock Sales, Rental Property, and more. Bilingual Enrolled Agent serving the Bay Area."
      />
      <section className="bg-primary py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-accent text-sm font-semibold uppercase tracking-wider mb-3">
              {t("Individual Tax Return", "개인 세금보고")}
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
              {t("Accurate, Clear, and Personalized Individual Tax Filing", "정확하고 이해하기 쉬운 개인 세금보고")}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-primary-foreground/70 text-lg max-w-2xl mx-auto">
              {t(
                "We handle complex returns — W-2, 1099, stock, crypto, rental, and more — in English and Korean.",
                "W-2, 1099, 주식, 가상화폐, 임대 등 복잡한 세금보고를 영어와 한국어로 처리합니다."
              )}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Income Categories */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground text-center mb-3">
              {t("What We Handle", "취급하는 세금 항목")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              {t("We prepare comprehensive federal and California state tax returns.", "연방 및 캘리포니아 주 세금보고를 종합적으로 준비합니다.")}
            </motion.p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((cat, i) => (
                <motion.div key={i} variants={fadeUp} className="bg-card border border-border rounded-xl p-5 hover:border-accent/40 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-primary/8 flex items-center justify-center text-primary shrink-0">
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-foreground text-sm">{t(cat.titleEn, cat.titleKo)}</h3>
                  </div>
                  <ul className="space-y-2">
                    {cat.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-accent mt-0.5 shrink-0" />
                        <span className="text-xs text-muted-foreground">{t(item.en, item.ko)}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Process */}
      <section className="bg-muted py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground text-center mb-10">
              {t("How the Process Works", "세금보고 절차")}
            </motion.h2>
            <div className="space-y-4">
              {process.map((step, i) => (
                <motion.div key={i} variants={fadeUp} className="flex items-start gap-4 bg-white rounded-xl border border-border p-5">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-bold shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-foreground text-sm leading-relaxed pt-1">{t(step.en, step.ko)}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground mb-4">
              {t("Ready to File Accurately?", "정확한 세금보고를 시작할 준비가 되셨나요?")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground mb-8">
              {t("Contact us to get started with your individual tax return.", "개인 세금보고를 시작하려면 지금 문의하세요.")}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity shadow-md">
                {t("Get Started", "시작하기")} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
